/**
 * V2 Action Bar Section Field Configuration
 *
 * V2 equivalent of footerSectionConfig.ts.
 * Maps V2 StepNode.content.actionBar paths.
 *
 * V2 path changes from V1:
 * - footer.button1 → content.actionBar.primary
 * - footer.button2 → content.actionBar.secondary
 * - footer.button1.content.l10n → content.actionBar.primary.content.translations
 * - footer.button1.styles → content.actionBar.primary.appearance
 * - button kind: 'text'→'label', 'image'→'icon'
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import {
  createTextStyleFields,
  createButtonContainerStyleFields,
  createV2ButtonAppearanceFields,
  createV2IconFields,
  remapTextFieldsForV2,
  remapButtonStyleFieldsForV2,
} from './atomicFields';
import { createV2ButtonStyleGroups } from './v2ButtonStyleGroups';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';

/**
 * V2 Primary Button field configuration
 *
 * Path: content.actionBar.primary
 * Translations: content.actionBar.primary.content.translations
 * Text styles: content.actionBar.primary.content.appearance
 * Button styles: content.actionBar.primary.appearance
 */
export const V2_PRIMARY_BUTTON_CONFIG: FieldConfig = {
  path: 'content.actionBar.primary',
  label: 'Primary Button',
  placeholder: 'Continue',
  type: 'text',
  l10nPath: 'content.actionBar.primary.content',
  defaultValue: 'Continue',
  disableBehavior: 'null',
  guideKey: 'screenedit-action-bar-primary-label',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.actionBar.primary.content')),
  buttonStyleFields: [
    ...remapButtonStyleFieldsForV2(createButtonContainerStyleFields('content.actionBar.primary')),
    ...remapButtonStyleFieldsForV2(createV2ButtonAppearanceFields('content.actionBar.primary')),
    ...createV2IconFields('content.actionBar.primary'),
  ],
  buttonStyleGroups: createV2ButtonStyleGroups('content.actionBar.primary'),
};

/**
 * V2 Secondary Button field configuration
 *
 * Path: content.actionBar.secondary
 * Translations: content.actionBar.secondary.content.translations
 */
export const V2_SECONDARY_BUTTON_CONFIG: FieldConfig = {
  path: 'content.actionBar.secondary',
  label: 'Secondary Button',
  placeholder: 'Log in',
  type: 'text',
  l10nPath: 'content.actionBar.secondary.content',
  defaultValue: 'Secondary action',
  disableBehavior: 'null',
  guideKey: 'screenedit-action-bar-secondary-label',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.actionBar.secondary.content')),
  buttonStyleFields: [
    ...remapButtonStyleFieldsForV2(createButtonContainerStyleFields('content.actionBar.secondary')),
    ...remapButtonStyleFieldsForV2(createV2ButtonAppearanceFields('content.actionBar.secondary')),
    ...createV2IconFields('content.actionBar.secondary'),
  ],
  buttonStyleGroups: createV2ButtonStyleGroups('content.actionBar.secondary'),
};

/**
 * All V2 action bar field configurations in render order
 */
export const V2_ACTION_BAR_FIELD_CONFIGS: FieldConfig[] = [
  V2_PRIMARY_BUTTON_CONFIG,
  V2_SECONDARY_BUTTON_CONFIG,
];
